import { useMemo } from 'react';
import { useCnpjAlphanumeric } from '@ibk/shared-ui';
import { isValidCpf, sanitizeAccountMaskAware } from '@ibk/shared-utils';
import { AccountTypeAcronym, Currency, DocumentTypeDescription, PixKeyTypeDescription, PixType, TransactionSource, } from '../../enums';
import { formatAsRegionalPhoneCode } from '../../utils/pixFormatters';
import { toAccountTypeAcronym, toPixBatchItems, toPixKeyTypeDescription } from '../../utils/pixKeyUtils';
import { usePostPixAgencyAccount } from '../usePostPixAgencyAccount';
import { usePostPixBatchTransfer } from '../usePostPixBatchTransfer';
import { usePostSchedulePix } from '../usePostSchedulePix';
import { usePostTransferPix } from '../usePostTransferPix';
export function usePostPix() {
    const { isValidCnpj } = useCnpjAlphanumeric();
    const { postTransferPix, pix, error: pixError, loading: pixLoading } = usePostTransferPix();
    const { postPixAgencyAccount, pix: pixAgencyAccount, error: pixAgencyAccountError, loading: pixAgencyAccountLoading, } = usePostPixAgencyAccount();
    const { postSchedulePix, pixScheduled, error: pixScheduledError, loading: pixScheduledLoading, } = usePostSchedulePix();
    const { postPixBatchTransfer, result: pixBatchAccepted, error: pixBatchProcessingError, loading: pixBatchLoading, } = usePostPixBatchTransfer();
    const loading = useMemo(() => pixLoading || pixAgencyAccountLoading || pixScheduledLoading || pixBatchLoading, [pixLoading, pixAgencyAccountLoading, pixScheduledLoading, pixBatchLoading]);
    async function postTransferPixKey(credential, genericPixKey, transactionStartAt, rethrow) {
        const key = genericPixKey.type === PixKeyTypeDescription.PHONE
            ? formatAsRegionalPhoneCode(genericPixKey.key)
            : genericPixKey.key;
        const type = toPixKeyTypeDescription(genericPixKey.type, genericPixKey.key, { isValidCnpj });
        const counterPartyDocumentType = isValidCpf(genericPixKey.receiverDocumentNumber)
            ? DocumentTypeDescription.CPF
            : DocumentTypeDescription.CNPJ;
        const counterpartyAccountType = genericPixKey.accountTypeAcronym ??
            toAccountTypeAcronym(genericPixKey.accountType) ??
            AccountTypeAcronym.CHECKING_ACCOUNT;
        if (genericPixKey.type === PixType.AGENCY || genericPixKey.type === PixKeyTypeDescription.AGENCY) {
            return (await postPixAgencyAccount({
                credential: credential,
                amount: genericPixKey.amount,
                currency: Currency.BRL,
                counterPartyName: genericPixKey.receiverName,
                counterPartyDocumentType: counterPartyDocumentType,
                counterPartyDocument: genericPixKey.receiverDocumentNumber,
                counterPartyBankAccount: sanitizeAccountMaskAware(genericPixKey.account),
                counterPartyBankBranch: genericPixKey.branch,
                counterPartyBankCode: genericPixKey.bankCode,
                counterPartyAccountType: genericPixKey.accountTypeAcronym,
                accountPayablesId: genericPixKey.accountPayablesId,
                description: genericPixKey.receiverName,
                confirmDuplicate: genericPixKey.confirmDuplicate,
            }, transactionStartAt, rethrow));
        }
        else {
            return (await postTransferPix({
                credential: credential,
                amount: genericPixKey.amount,
                currency: Currency.BRL,
                source: TransactionSource.FINTECH_IBK,
                pixDetails: {
                    keyType: type,
                    key: key,
                    description: genericPixKey.description ?? '',
                    counterpartyName: genericPixKey.receiverName,
                    counterpartyDocumentType: counterPartyDocumentType,
                    counterpartyDocument: genericPixKey.receiverDocumentNumber,
                    counterpartyBank: genericPixKey.bankName ?? '',
                    counterpartyBankAccount: sanitizeAccountMaskAware(genericPixKey.account),
                    counterpartyBankBranch: genericPixKey.branch,
                    counterpartyBankCode: genericPixKey.bankCode,
                    counterpartyAccountType: counterpartyAccountType,
                    endToEndId: genericPixKey.endToEndId,
                    transactionIdentification: genericPixKey.transactionIdentification,
                    qrCodeType: genericPixKey.qrCodeType,
                },
                accountPayablesId: genericPixKey.accountPayablesId,
                confirmDuplicate: genericPixKey.confirmDuplicate,
            }, transactionStartAt, rethrow));
        }
    }
    async function postPixBatch(credential, title, batchOfGenericPixKey, transactionStartAt, scheduledDate) {
        const items = toPixBatchItems(batchOfGenericPixKey, scheduledDate, { isValidCnpj });
        return await postPixBatchTransfer({ title, items }, credential, transactionStartAt);
    }
    async function postSchedulePixKey(credential, genericPixKey, transactionStartAt, scheduledDate, rethrow) {
        const counterpartyAccountType = genericPixKey.accountTypeAcronym ??
            toAccountTypeAcronym(genericPixKey.accountType) ??
            AccountTypeAcronym.CHECKING_ACCOUNT;
        return (await postSchedulePix({
            credential: credential,
            amount: genericPixKey.amount,
            currency: Currency.BRL,
            source: TransactionSource.FINTECH_IBK,
            accountPayablesId: genericPixKey.accountPayablesId,
            paymentDate: scheduledDate,
            pixDetails: {
                key: genericPixKey.type === PixKeyTypeDescription.PHONE
                    ? formatAsRegionalPhoneCode(genericPixKey.key)
                    : genericPixKey.key,
                keyType: toPixKeyTypeDescription(genericPixKey.type, genericPixKey.key, { isValidCnpj }),
                description: genericPixKey.description ?? '',
                counterpartyName: genericPixKey.receiverName,
                counterpartyDocumentType: isValidCpf(genericPixKey.receiverDocumentNumber)
                    ? DocumentTypeDescription.CPF
                    : DocumentTypeDescription.CNPJ,
                counterpartyDocument: genericPixKey.receiverDocumentNumber,
                counterpartyBankName: genericPixKey.bankName,
                counterpartyBankAccount: genericPixKey.account,
                counterpartyBankBranch: genericPixKey.branch,
                counterpartyBankCode: genericPixKey.bankCode,
                counterpartyAccountType: counterpartyAccountType,
                transactionIdentification: genericPixKey.transactionIdentification,
                qrCodeType: genericPixKey.qrCodeType,
            },
        }, transactionStartAt, rethrow));
    }
    async function postPix(credential, transactionStartAt, scheduledDate, genericPixKey, batchOfGenericPixKey, title) {
        if (genericPixKey) {
            if (scheduledDate) {
                return await postSchedulePixKey(credential, genericPixKey, transactionStartAt, scheduledDate);
            }
            return await postTransferPixKey(credential, genericPixKey, transactionStartAt);
        }
        if (batchOfGenericPixKey) {
            return await postPixBatch(credential, title, batchOfGenericPixKey, transactionStartAt, scheduledDate);
        }
    }
    return {
        postPix,
        pix,
        pixAgencyAccount,
        pixScheduled,
        pixError,
        pixAgencyAccountError,
        pixScheduledError,
        pixBatchAccepted,
        pixBatchProcessingError,
        pixBatchLoading,
        loading,
    };
}
